import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, CornerDownRight, History, Layers, PenLine, UserMinus, UserPlus, UserRoundPen } from 'lucide-react';
import { Avatar } from '../../../components/ui';
import { POST_LABEL, samePerson } from '../chart';
import { REORG_LABEL, govDate, sinceLabel } from '../format';
import type { GovNodeView, GovOfficeHolder, GovUpdateView } from '../types';

/**
 * Who the directory records in post at a body, most senior first — and,
 * beside it, what research found, so the gap between the two is the first
 * thing a reviewer sees rather than something to work out.
 */
export const InPost: React.FC<{ node: GovNodeView; holders: GovOfficeHolder[] }> = ({ node, holders }) => {
  const research = node.head?.name ? node.head : null;
  const agreed = research ? holders.find((holder) => samePerson(holder.name, research.name)) : undefined;

  return (
    <div className="space-y-2">
      {holders.length === 0 ? (
        <p className="text-[12.5px] text-neutral-400">
          {node.organizationId ? 'Nobody is recorded in post here.' : 'Not in the directory yet.'}
        </p>
      ) : (
        <ul className="divide-y divide-neutral-100 overflow-hidden rounded-xl border border-neutral-200">
          {holders.map((holder) => (
            <li key={`${holder.stakeholderId}-${holder.role}`} className="flex items-center gap-2.5 px-3 py-2">
              <Avatar name={holder.name} kind="stakeholder" size="xs" />
              <span className="min-w-0 flex-1">
                <Link
                  to={`/stakeholders/${holder.stakeholderId}`}
                  className="block truncate text-[13px] text-ink hover:underline hover:decoration-neutral-300 hover:underline-offset-2"
                >
                  {holder.name}
                </Link>
                <span className="block truncate text-[11.5px] text-neutral-500">
                  {POST_LABEL[holder.post] && holder.post !== 'other' ? POST_LABEL[holder.post] : holder.role}
                  {holder.since ? ` · since ${govDate(holder.since)}` : ''}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
      {research && (
        <p className="text-[12px] leading-snug text-neutral-500">
          <span className="text-neutral-400">Research: </span>
          {research.name}
          {research.title ? `, ${research.title}` : ''}
          {research.since ? ` since ${govDate(research.since)}` : ''}
          {agreed ? (
            <>
              {' — '}
              <span className="text-emerald-700">recorded</span>
            </>
          ) : node.organizationId ? (
            <>
              {' — '}
              <span className="text-amber-700">not recorded</span>
            </>
          ) : null}

        </p>
      )}
    </div>
  );
};

const ICON: Record<GovUpdateView['kind'], React.ElementType> = {
  appointment: UserPlus,
  departure: UserMinus,
  correction: UserRoundPen,
  rename: PenLine,
  restructure: Building2,
  duplicate: Layers,
  move: CornerDownRight,
  former: History,
};

/** One line saying what an applied change did */
const didLine = (update: GovUpdateView): React.ReactNode => {
  const done = (update.applied ?? {}) as Record<string, any>;
  switch (update.kind) {
    case 'appointment':
      return (
        <>
          <span className="text-ink">{done.personLabel || update.person?.label}</span> — {done.role || update.roleTitle}
          {done.createdPerson ? ' (added to the directory)' : ''}
        </>
      );
    case 'departure':
      return (
        <>
          <span className="text-ink">{update.person?.label}</span> left — {update.roleTitle}
        </>
      );
    case 'correction':
      return (
        <>
          <span className="text-ink">{update.person?.label}</span>: in post since{' '}
          {govDate(done.after?.startDate ?? update.effectiveDate)}
        </>
      );
    case 'rename':
      return (
        <>
          “{done.previous ?? update.changes[0]?.from}” <ArrowRight className="inline h-3 w-3" strokeWidth={1.75} />{' '}
          <span className="text-ink">“{done.name ?? update.changes[0]?.to}”</span>
        </>
      );
    case 'duplicate':
      return (
        <>
          {((done.folded as string[] | undefined) ?? update.duplicate?.fold.map((entry) => entry.name) ?? [])
            .map((name) => `“${name}”`)
            .join(', ')}{' '}
          merged into <span className="text-ink">{done.kept?.name ?? update.duplicate?.keep.name}</span>
        </>
      );
    case 'former':
      return (
        <>
          <span className="text-ink">{done.personLabel ?? update.person?.label}</span>, {update.roleTitle} from{' '}
          {govDate(done.since ?? update.effectiveDate)} to{' '}
          {govDate(done.until ?? update.changes.find((change) => change.field === 'endDate')?.to)}
        </>
      );
    case 'move':
      return (
        <>
          Now under{' '}
          <span className="text-ink">{update.changes.find((change) => change.field === 'parent')?.to}</span>
        </>
      );
    case 'restructure':
      return (
        <>
          {update.reorganisation ? REORG_LABEL[update.reorganisation.how] : 'Reorganised'} into{' '}
          <span className="text-ink">
            {(update.reorganisation?.into ?? []).map((org) => org.label).join(', ')}
          </span>
        </>
      );
    default:
      return update.summary;
  }
};

/**
 * What has been done at this body, newest first: who came and went, when
 * it was renamed or reorganised, and what each change ended.
 */
export const EntityHistory: React.FC<{ history: GovUpdateView[] }> = ({ history }) => {
  if (!history.length) {
    return <p className="text-[12.5px] text-neutral-400">No changes applied here yet.</p>;
  }
  return (
    <ol className="relative space-y-3 before:absolute before:bottom-1 before:left-[7px] before:top-1 before:w-px before:bg-neutral-200">
      {history.map((update) => {
        const Icon = ICON[update.kind];
        const closed = ((update.applied as any)?.closed ?? []) as Array<{
          personLabel?: string;
          role: string;
          endDate?: string;
        }>;
        return (
          <li key={update.id} className="relative flex gap-2.5 pl-0">
            <span className="relative z-10 mt-0.5 inline-flex h-[15px] w-[15px] flex-shrink-0 items-center justify-center rounded-full bg-white ring-1 ring-neutral-200">
              <Icon className="h-2.5 w-2.5 text-neutral-500" strokeWidth={2} />
            </span>
            <div className="min-w-0 flex-1 text-[12.5px] leading-snug text-neutral-600">
              <p>{didLine(update)}</p>
              {update.kind !== 'departure' &&
                closed
                  .filter((role) => role.personLabel)
                  .map((role) => (
                    <p key={`${role.personLabel}-${role.role}`} className="text-[12px] text-neutral-500">
                      ended {role.personLabel} — {role.role}
                      {role.endDate ? `, ${govDate(role.endDate)}` : ''}
                    </p>
                  ))}
              <p className="mt-0.5 text-[11.5px] text-neutral-400">
                {update.effectiveDate ? `${govDate(update.effectiveDate)} · ` : ''}
                applied {sinceLabel(update.reviewedAt ?? update.createdAt)}
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
};
